import React, { useEffect, useMemo, useState } from "react";
import PanelDashboardLayout from "../PanelDashboardLayout";
import {
  getPromotionAcademicYearsViaAdmin,
  getPromotionCohortsViaAdmin,
  getAllProgramsViaAdmin,
  createAcademicYearViaAdmin,
  setCurrentAcademicYearViaAdmin,
  createCohortViaAdmin,
} from "../../../utils/Api";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useForm } from "react-hook-form";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

// Academic Setup — the authoring side of the annual rollover. Create/set the current Academic
// Year, create new admission Cohorts, and (via the checklist) walk the admin through the exact
// order that opens First-Year admissions for a new intake year. Batch <-> cohort re-pointing
// lives on the Batches page (per-row "Assign Cohort").

const tableCustomStyles = {
  headRow: { style: { color: "#fff", backgroundColor: "#0F1015" } },
};

const AcademicSetup = () => {
  const [academicYears, setAcademicYears] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set-as-current per-row spinner (keyed by AY id).
  const [settingCurrentId, setSettingCurrentId] = useState(null);

  // Create Academic Year modal
  const [showAYModal, setShowAYModal] = useState(false);
  const [savingAY, setSavingAY] = useState(false);
  const ayForm = useForm();

  // Create Cohort modal
  const [showCohortModal, setShowCohortModal] = useState(false);
  const [savingCohort, setSavingCohort] = useState(false);
  const cohortForm = useForm();
  const selectedProgramId = cohortForm.watch("programId");
  const selectedAdmissionYear = cohortForm.watch("admissionYear");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ayRes, cohortRes, progRes] = await Promise.all([
        getPromotionAcademicYearsViaAdmin(),
        getPromotionCohortsViaAdmin(),
        getAllProgramsViaAdmin(),
      ]);
      setAcademicYears(ayRes?.academicYears || []);
      setCohorts(cohortRes?.cohorts || []);
      setPrograms(progRes?.programs || []);
    } catch (error) {
      console.error("Error fetching academic setup data:", error);
      toast.error(error?.response?.data?.error || "Error loading academic setup");
    } finally {
      setLoading(false);
    }
  };

  const currentAY = useMemo(
    () => academicYears.find((ay) => ay.isCurrent) || null,
    [academicYears]
  );

  // ---- Create Academic Year -------------------------------------------------------------
  const openAYModal = () => {
    const nextStart = currentAY ? currentAY.startYear + 1 : new Date().getFullYear();
    ayForm.reset({
      startYear: nextStart,
      endYear: nextStart + 1,
      name: `${nextStart}-${nextStart + 1}`,
      isCurrent: false,
    });
    setShowAYModal(true);
  };
  const closeAYModal = () => setShowAYModal(false);

  const submitAY = async (data) => {
    setSavingAY(true);
    try {
      await createAcademicYearViaAdmin({
        name: (data.name || "").trim(),
        startYear: Number(data.startYear),
        endYear: Number(data.endYear),
        isCurrent: !!data.isCurrent,
      });
      toast.success("Academic year created");
      closeAYModal();
      fetchData();
    } catch (error) {
      console.error("Error creating academic year:", error);
      toast.error(error?.response?.data?.error || "Error creating academic year");
    } finally {
      setSavingAY(false);
    }
  };

  const handleSetCurrent = async (ay) => {
    if (ay.isCurrent) return;
    if (
      !window.confirm(
        `Set "${ay.name}" as the current academic year? New admissions will be anchored to it.`
      )
    ) {
      return;
    }
    setSettingCurrentId(ay._id);
    try {
      await setCurrentAcademicYearViaAdmin(ay._id);
      toast.success(`"${ay.name}" is now the current academic year`);
      fetchData();
    } catch (error) {
      console.error("Error setting current academic year:", error);
      toast.error(error?.response?.data?.error || "Error setting current academic year");
    } finally {
      setSettingCurrentId(null);
    }
  };

  // ---- Create Cohort --------------------------------------------------------------------
  const openCohortModal = () => {
    const admissionYear = currentAY ? currentAY.startYear : new Date().getFullYear();
    cohortForm.reset({
      programId: "",
      admissionYear,
      durationYears: "",
      name: "",
    });
    setShowCohortModal(true);
  };
  const closeCohortModal = () => setShowCohortModal(false);

  // When program or admissionYear changes, prefill durationYears + suggest a cohort name.
  useEffect(() => {
    if (!showCohortModal) return;
    const prog = programs.find((p) => p._id === selectedProgramId);
    if (prog) {
      if (prog.durationYears != null) {
        cohortForm.setValue("durationYears", prog.durationYears);
      }
      const label = prog.programName || prog.name || "";
      const slug = label.trim().toUpperCase().replace(/\s+/g, "-");
      if (slug && selectedAdmissionYear) {
        cohortForm.setValue("name", `${slug}-${selectedAdmissionYear}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramId, selectedAdmissionYear, showCohortModal]);

  const submitCohort = async (data) => {
    setSavingCohort(true);
    try {
      const payload = {
        name: (data.name || "").trim(),
        programId: data.programId,
        admissionYear: Number(data.admissionYear),
      };
      if (data.durationYears !== "" && data.durationYears != null) {
        payload.durationYears = Number(data.durationYears);
      }
      await createCohortViaAdmin(payload);
      toast.success("Cohort created");
      closeCohortModal();
      fetchData();
    } catch (error) {
      console.error("Error creating cohort:", error);
      toast.error(error?.response?.data?.error || "Error creating cohort");
    } finally {
      setSavingCohort(false);
    }
  };

  // ---- Rollover checklist (live state) ---------------------------------------------------
  const nextIntakeYear = currentAY ? currentAY.startYear : null;
  const hasNextCurrentAY = !!currentAY;
  // A cohort whose admissionYear == the current AY start is "this year's new intake".
  const hasNewIntakeCohort =
    nextIntakeYear != null &&
    cohorts.some((c) => Number(c.admissionYear) === Number(nextIntakeYear));

  const ChecklistRow = ({ done, children }) => (
    <div className="d-flex align-items-start mb-2" style={{ gap: 8 }}>
      {done ? (
        <CheckCircleIcon fontSize="small" style={{ color: "#28a745", marginTop: 2 }} />
      ) : (
        <RadioButtonUncheckedIcon fontSize="small" style={{ color: "#adb5bd", marginTop: 2 }} />
      )}
      <span>{children}</span>
    </div>
  );

  // ---- Columns --------------------------------------------------------------------------
  const ayColumns = [
    { name: "SL. No", selector: (row, i) => ++i, width: "70px" },
    { name: "Name", selector: (row) => row?.name || "-", sortable: true },
    { name: "Start", selector: (row) => row?.startYear ?? "-", sortable: true, width: "100px" },
    { name: "End", selector: (row) => row?.endYear ?? "-", width: "100px" },
    {
      name: "Current",
      cell: (row) =>
        row?.isCurrent ? (
          <Chip label="Current" size="small" sx={{ backgroundColor: "#d4edda", color: "#155724" }} />
        ) : (
          "-"
        ),
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) =>
        row?.isCurrent ? (
          <span className="text-muted">—</span>
        ) : (
          <Button
            size="small"
            variant="outlined"
            disabled={settingCurrentId === row._id}
            startIcon={
              settingCurrentId === row._id ? (
                <CircularProgress size={14} color="inherit" />
              ) : null
            }
            onClick={() => handleSetCurrent(row)}
          >
            {settingCurrentId === row._id ? "Setting..." : "Set as Current"}
          </Button>
        ),
      width: "180px",
    },
  ];

  const cohortColumns = [
    { name: "SL. No", selector: (row, i) => ++i, width: "70px" },
    { name: "Cohort", selector: (row) => row?.name || "-", sortable: true },
    { name: "Program", selector: (row) => row?.program?.name || "-", sortable: true },
    {
      name: "Admission Year",
      selector: (row) => row?.admissionYear ?? "-",
      sortable: true,
      width: "150px",
    },
    { name: "Duration", selector: (row) => row?.durationYears ?? "-", width: "110px" },
    {
      name: "Students",
      selector: (row) => row?.currentStudentCount ?? 0,
      width: "110px",
    },
  ];

  return (
    <PanelDashboardLayout>
      <div>
        {/* Rollover checklist */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Annual Rollover</h4>
              <p className="card-description">
                Follow these steps in order to promote students and open First-Year
                admissions for a new intake year.
              </p>
              <ChecklistRow done={hasNextCurrentAY}>
                <strong>1. Academic Year.</strong> Create the new academic year and set it
                as current.{" "}
                {currentAY ? (
                  <span className="text-success">Current: {currentAY.name}.</span>
                ) : (
                  <span className="text-danger">No current academic year set.</span>
                )}
              </ChecklistRow>
              <ChecklistRow done={false}>
                <strong>2. Promote cohorts.</strong> Advance existing cohorts one year on the{" "}
                <NavLink to="/panel-admin/promotions">Promotions</NavLink> page. Final-year
                cohorts graduate only when their results are verified (policy must be VERIFIED).
              </ChecklistRow>
              <ChecklistRow done={hasNewIntakeCohort}>
                <strong>3. New cohorts.</strong> Create the new admission cohorts for this
                year (e.g. <code>BA-LLB-{nextIntakeYear ?? "YYYY"}</code>) in the section below.
              </ChecklistRow>
              <ChecklistRow done={false}>
                <strong>4. Re-point FY batches.</strong> On the{" "}
                <NavLink to="/panel-admin/batches">Batches</NavLink> page, use{" "}
                <em>Assign Cohort</em> to point each First-Year batch slot at the new cohort
                from step 3.
              </ChecklistRow>
              <ChecklistRow done={false}>
                <strong>5. Admit.</strong> Only after steps 1–4 — new students then
                automatically join the correct cohort and academic year.
              </ChecklistRow>
            </div>
          </div>
        </div>

        {/* Academic Years */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="card-title mb-0">Academic Years</h4>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CalendarMonthIcon />}
                  onClick={openAYModal}
                >
                  Create Academic Year
                </Button>
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={ayColumns}
                  data={academicYears}
                  customStyles={tableCustomStyles}
                  pagination
                  progressPending={loading}
                  noDataComponent={<div className="p-4">No academic years yet.</div>}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cohorts */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="card-title mb-0">Cohorts</h4>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<GroupsIcon />}
                  onClick={openCohortModal}
                >
                  Create Cohort
                </Button>
              </div>
              <div className="table-responsive">
                <DataTable
                  columns={cohortColumns}
                  data={cohorts}
                  customStyles={tableCustomStyles}
                  pagination
                  progressPending={loading}
                  noDataComponent={<div className="p-4">No cohorts yet.</div>}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Create Academic Year Dialog */}
        <Dialog open={showAYModal} onClose={closeAYModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonthIcon color="primary" />
              <span>Create Academic Year</span>
            </Box>
          </DialogTitle>
          <form onSubmit={ayForm.handleSubmit(submitAY)}>
            <DialogContent dividers>
              <div className="form-group">
                <label>Start Year</label>
                <input
                  className="form-control p_input"
                  type="number"
                  {...ayForm.register("startYear", { required: true })}
                  onChange={(e) => {
                    const s = Number(e.target.value);
                    ayForm.setValue("startYear", e.target.value);
                    if (!Number.isNaN(s)) {
                      ayForm.setValue("endYear", s + 1);
                      ayForm.setValue("name", `${s}-${s + 1}`);
                    }
                  }}
                />
                {ayForm.formState.errors.startYear && (
                  <p className="text-danger">Start year is required</p>
                )}
              </div>
              <div className="form-group">
                <label>End Year</label>
                <input
                  className="form-control p_input"
                  type="number"
                  {...ayForm.register("endYear", { required: true })}
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  className="form-control p_input"
                  type="text"
                  placeholder="e.g. 2026-2027"
                  {...ayForm.register("name", { required: true })}
                />
                {ayForm.formState.errors.name && (
                  <p className="text-danger">Name is required</p>
                )}
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="ay-isCurrent"
                  {...ayForm.register("isCurrent")}
                />
                <label className="form-check-label" htmlFor="ay-isCurrent">
                  Set as current academic year
                </label>
              </div>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeAYModal} color="inherit" type="button">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={savingAY}
                startIcon={savingAY && <CircularProgress size={16} color="inherit" />}
              >
                {savingAY ? "Creating..." : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Create Cohort Dialog */}
        <Dialog open={showCohortModal} onClose={closeCohortModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <GroupsIcon color="primary" />
              <span>Create Cohort</span>
            </Box>
          </DialogTitle>
          <form onSubmit={cohortForm.handleSubmit(submitCohort)}>
            <DialogContent dividers>
              <div className="form-group">
                <label>Program</label>
                <select
                  className="form-control p_input"
                  {...cohortForm.register("programId", { required: true })}
                >
                  <option value="">Select Program</option>
                  {programs.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.programName || p.name}
                    </option>
                  ))}
                </select>
                {cohortForm.formState.errors.programId && (
                  <p className="text-danger">Program is required</p>
                )}
              </div>
              <div className="form-group">
                <label>Admission Year</label>
                <input
                  className="form-control p_input"
                  type="number"
                  {...cohortForm.register("admissionYear", { required: true })}
                />
                {cohortForm.formState.errors.admissionYear && (
                  <p className="text-danger">Admission year is required</p>
                )}
              </div>
              <div className="form-group">
                <label>Duration (years)</label>
                <input
                  className="form-control p_input"
                  type="number"
                  placeholder="Prefilled from program"
                  {...cohortForm.register("durationYears")}
                />
                <small className="text-muted">
                  Auto-filled from the selected program; override only if needed.
                </small>
              </div>
              <div className="form-group">
                <label>Cohort Name</label>
                <input
                  className="form-control p_input"
                  type="text"
                  placeholder="e.g. BA-LLB-2026"
                  {...cohortForm.register("name", { required: true })}
                />
                {cohortForm.formState.errors.name && (
                  <p className="text-danger">Cohort name is required</p>
                )}
              </div>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeCohortModal} color="inherit" type="button">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={savingCohort}
                startIcon={savingCohort && <CircularProgress size={16} color="inherit" />}
              >
                {savingCohort ? "Creating..." : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </div>
    </PanelDashboardLayout>
  );
};

export default AcademicSetup;
